import { Moment } from "moment";
import moment = require("moment");

import { LinkObject } from "@teamprevent/common/model";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";

import { ImmunisationTaskType } from "../consultations/models";

export type ImmunisationClearCertificateModel = {
    employeeDetails: {
        title: string;
        firstName: string;
        lastName: string;
        dateOfBirth: Moment;
        customerName: string;
        customerNumber: string;
        orgLevel1: string;
        orgLevel2: string;
        orgLevel3: string;
        orgLevel4: string;
        jobTitle: string;
    };
    outcome: string;
    clinicalComments: string;
    immunisationClearDate: Moment;
    completedByName: string;
    vaccinationStatus: string;
}

export const parseImmunisationClearCertificateModel = (model: ImmunisationClearCertificateModel): ImmunisationClearCertificateModel => {
    return {
        ...model,
        employeeDetails: {
            ...model.employeeDetails,
            dateOfBirth: moment(model.employeeDetails.dateOfBirth),
        },
        immunisationClearDate: moment(model.immunisationClearDate)
    };
};

export interface ImmunisationForCase {
    readonly typeId: string;
    readonly name: string;
    readonly dateCompleted: moment.Moment | undefined;
    readonly immunisationDecision: ImmunisationDecision;
}

export enum ImmunisationDecision {
    Required = "Required",
    RequiredEvidenceProvided = "RequiredEvidenceProvided",
    RequiredNoEvidence = "RequiredNoEvidence"
}

export const ImmunisationDecisions = [
    { text: "Not required for role", value: ImmunisationDecision.Required },
    { text: "Required for role - evidence provided and added to immunisation record", value: ImmunisationDecision.RequiredEvidenceProvided },
    { text: "Required for role - no evidence provided", value: ImmunisationDecision.RequiredNoEvidence }
];

export type ImmunisationDecisionResult = Record<string, ImmunisationDecision>;

export enum EvidenceType {
    documentaryEvidenceViewed = "documentaryEvidenceViewed",
    historyOfDisease = "historyOfDisease",
    bcgScar = "bcgScar",
    historyOfvaccination = "historyOfvaccination",
    historyOfPreviousVaricellaInfectionInTheUK = "historyOfPreviousVaricellaInfectionInTheUK"
}

export const EvidenceTypes = [
    { text: "Documentary Evidence Viewed", value: EvidenceType.documentaryEvidenceViewed },
    { text: "History of Disease", value: EvidenceType.historyOfDisease },
    { text: "BCG Scar", value: EvidenceType.bcgScar },
    { text: "History of Vaccination", value: EvidenceType.historyOfvaccination },
    { text: "History of previous Varicella infection in the UK", value: EvidenceType.historyOfPreviousVaricellaInfectionInTheUK }
];

export enum NextStep {
    takeBloodNow = "takeBloodNow",
    giveVaccineNow  = "giveVaccineNow ",
    bookBloodTest = "bookABloodTest",
    bookVaccine = "bookAVaccine",
    chaseEmployee = "chaseEmployee"
}

export const NextSteps = [
    {
        text: "Take Blood Now",
        value: NextStep.takeBloodNow,
        taskTypeId: "ImmunisationBloodTestTask",
        taskName: "Immunisation blood test",
        isBookTask: false,
        immunisationTaskType: ImmunisationTaskType.BloodTest
    },
    {
        text: "Give Vaccine Now",
        value: NextStep.giveVaccineNow,
        taskTypeId: "ImmunisationVaccineTask",
        taskName: "Vaccination appointment",
        isBookTask: false,
        immunisationTaskType: ImmunisationTaskType.Vaccine
    },
    {
        text: "Book a blood test",
        value: NextStep.bookBloodTest,
        taskTypeId: "BookImmunisationBloodTestTask",
        taskName: "Book blood test appointment",
        isBookTask: true,
        immunisationTaskType: ImmunisationTaskType.BloodTest
    },
    {
        text: "Book a vaccine",
        value: NextStep.bookVaccine,
        taskTypeId: "BookImmunisationVaccineTask",
        taskName: "Book vaccination appointment",
        isBookTask: true,
        immunisationTaskType: ImmunisationTaskType.Vaccine
    },
    {
        text: "General Admin Task: Chase Employee for Immunisation History",
        value: NextStep.chaseEmployee,
        taskTypeId: "GeneralAdminTask",
        taskName: "Chase Employee for Immunisation History",
        isBookTask: false,
        taskCode: "Chase Employee for Immunisation History"
    }
];

export interface ImmunisationListModel {
    readonly id: string;
    readonly notes: string;
    readonly immunisationType: LinkObject;
    readonly dateCompleted: Moment;
    readonly recordType: string;
    readonly outcome: string;
    readonly identityValidation: boolean;
    readonly employeeRecordId: string;
    readonly completedById: string;
    readonly completedByName: string;
    readonly details: string;
}

export const IdentityValidationOptions: DropdownOption[] = [
    { text: "Yes", value: String(true) },
    { text: "No", value: String(false) }
];

export const getIdentityValidationOptionsValue = (value: boolean): string => {
    return IdentityValidationOptions.find(o => o.value === JSON.stringify(value))?.text;
};
